import React, { useEffect, useRef, useState } from "react";
import {
  Collapse,
  Form,
  Button,
  message,
  Typography,
  Space,
  Row,
  Col,
} from "antd";
import ConsultantForm from "../CommonComponents/ConsultantForm";
import PatientProfile from "../CommonComponents/PatientProfile";
import ClinicalImpression from "../CommonComponents/ClinicalImpression";
import HistoryIllness from "../CommonComponents/HistoryIllness";
import Medication from "../CommonComponents/Medication";
import Nebulization from "../CommonComponents/Nebulization";
import Vitals from "../CommonComponents/Vitals";
import DischargeFunctionalStatus from "../DischargeFunctionalStatus";
import Comments from "../CommonComponents/Comments";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import dayjs from "dayjs";
import {
  getListReviewFormApi,
  getPatientDetailsApi,
  printFormApi,
  submitReviewFormApi,
  updateReviewFormApi,
} from "../../../../services/forms.services";
import { HistoryOutlined } from "@ant-design/icons";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../../IpEMR/Options/historyOptions";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
  printFormSummary,
} from "../formsKeys";
import ReviewClinicalProgress from "./ReviewClinicalProgress";
import ReviewFormScales from "./ReviewFormScales";
import ReviewModal from "./ReviewModal";
import useQuery from "../../../../hooks/useQuery";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import assessmentIcon from "../../../../assets/images/nurseIcons/assessment.svg";
import FormContentLoader from "../FormContentLoader";
import ScaledHtmlPreview from "../../../ui/ScaledHtmlPreview";
import "../ReviewForms.css";
import "../FormsIp.css";

const ReviewForms = ({ patientData, role }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const commonStyle = {
    // background: "#fff",
    boxShadow:
      "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
  };

  const [pressureSorePoints, setPressureSorePoints] = useState([]);
  const [referralFormData, setReferralFormData] = useState(null);
  const [editable, setEditable] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [reviewList, setReviewList] = useState([]);
  const [open, setOpen] = useState(false);
  const [displayForm, setDisplayForm] = useState(null);
  const [isNewForm, setIsNewForm] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const isActive = useQuery()?.status === "current";
  const isReceptionist = role === "receptionist";
  const isPhysio = role === "physiotherapist";
  const hasInitialAssessment = isPhysio
    ? patientData?.physio_initial_assessment
    : patientData?.doctor_intial_assessment;
  const canEditForm = isActive && !isReceptionist && editable;
  const formTitle = isPhysio ? "Physio review form" : "Review Form";
  const functionalStatusHeader = isPhysio
    ? "Functional Status at Referral"
    : "Functional Status at Discharge / Referral";

  const refreshPage = () => setRefresh((prev) => !prev);

  const applyReviewFormData = (record) => {
    if (!record?.data) return;
    form.setFieldsValue({
      ...record.data,
      clinical_progress: {
        ...record.data?.clinical_progress,
        date: record.data?.clinical_progress?.date
          ? dayjs(record.data.clinical_progress.date)
          : null,
      },
    });
    if (record.data?.pressure_sore) {
      setPressureSorePoints(record.data.pressure_sore);
    } else {
      setPressureSorePoints([]);
    }
  };

  const loadReviewFormDisplay = (formId) => {
    if (!formId) {
      setDisplayForm(null);
      return Promise.resolve();
    }
    return printFormApi(`form_type=review&form_id=${formId}`)
      .then((res) => {
        setDisplayForm(res?.data?.data ?? null);
      })
      .catch((err) => {
        console.log(err);
        setDisplayForm(null);
        message.error(
          err?.response?.data?.message || "Failed to load review form"
        );
      });
  };

  const fetchReviewList = () => {
    setIsContentReady(false);
    return getListReviewFormApi(Number(patientData?.admission?.id))
      .then((res) => {
        const list = res?.data || [];
        setReviewList(list);

        if (isNewForm) {
          setReferralFormData(null);
          setDisplayForm(null);
          setEditable(isActive && !isReceptionist);
        } else {
          const latestRecord = list[list.length - 1] ?? null;
          setReferralFormData(latestRecord);
          applyReviewFormData(latestRecord);
          if (isReceptionist || !isActive) {
            setEditable(false);
          } else {
            setEditable(latestRecord?.id ? false : true);
          }
          return loadReviewFormDisplay(latestRecord?.id);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsContentReady(true);
      });
  };

  useEffect(() => {
    if (patientData?.admission?.id && hasInitialAssessment) {
      fetchReviewList();
      getPatientDetailsApi(patientData?.admission?.id)
        .then((res) => {
          setDetails(res?.data);
        })
        .catch((err) => console.log(err));
    }
  }, [patientData?.admission?.id, hasInitialAssessment, refresh, isActive, isReceptionist]);

  useEffect(() => {
    if (referralFormData?.data) {
      applyReviewFormData(referralFormData);
    } else if (isNewForm) {
      form.resetFields();
      setPressureSorePoints([]);
    }
  }, [referralFormData]);

  const handleModalAction = (record, isEdit) => {
    form.resetFields();
    setDisplayForm(null);
    setReferralFormData(record);
    setOpen(false);
    setEditable(isEdit && !isReceptionist);
    setIsNewForm(false);
    setIsContentReady(false);

    applyReviewFormData(record);

    loadReviewFormDisplay(record?.id).finally(() => {
      setIsContentReady(true);
    });
  };

  const handleNewForm = () => {
    setIsNewForm(true);
    setReferralFormData(null);
    form.resetFields();
    setPressureSorePoints([]);
    setDisplayForm(null);
    setEditable(true);
    setIsContentReady(true);
  };

  const onFinish = (values) => {
    const formData = {
      patient: details?.data?.patient,
      diagnosis: details?.data?.diagnosis,
      ...(isPhysio
        ? {}
        : {
            medications: details?.data?.medications,
            vitals: details?.data?.vitals,
            nebulization: details?.data?.nebulization,
          }),
      past_medical_history: details?.data?.present_illness
        ? getPastMedicalHistoryFormSummary(
            pastMedicalOptions,
            details?.data?.present_illness
          )
        : "",
      known_allergies: details?.data?.present_illness
        ? getLabelTagsWithFormDescription(
            knownAllergiesOptions,
            details?.data?.present_illness
          )
        : "",
      past_surgical_history: details?.data?.present_illness?.surgical_history
        ? details?.data?.present_illness?.surgical_history
        : null,
      past_surgical_history_details: details?.data?.present_illness
        ?.surgical_history_details
        ? details?.data?.present_illness?.surgical_history_details
        : null,
      ...values,

      clinical_progress: {
        ...values?.clinical_progress,
        date: values?.clinical_progress?.date
          ? dayjs(values.clinical_progress.date).format("YYYY-MM-DD")
          : null, // Handle cases where the date is not provided
      },
      pressure_sore: isPhysio ? [] : pressureSorePoints,
    };
    if (referralFormData?.id) {
      updateReviewFormApi(referralFormData?.id, { data: formData })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          refreshPage();
          setReferralFormData((prev) => ({
           ...prev,
           data: formData,
         }));

        })
        .catch((err) => message.error(err?.response?.data?.message));
    } else {
      submitReviewFormApi({
        ip_admission: patientData?.admission?.id,
        data: formData,
      })
        .then((res) => {
          message.success(res?.data?.message);
          setIsNewForm(false);
          setEditable(false);
          fetchReviewList();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }

    // console.log(formData, "fo");
  };
  // console.log(reviewList, referralFormData);
  return (
    <>
      {!hasInitialAssessment ? (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={
                isPhysio
                  ? "Physiotherapist Initial Assessment Not Yet Submitted "
                  : "Initial Assessment Not Yet Submitted "
              }
              subtitle={"Kindly, submit initial assessment first"}
              image={assessmentIcon}
            />
          </Col>
        </Row>
      ) : (
        <div style={{ position: "relative" }}>
          <Typography.Title level={5} className="review-form-title">
            <div className="review-form-title-top">
              <span className="review-form-title-text">
                {formTitle} {referralFormData?.id}
                {reviewList?.length > 0 && (
                  <HistoryOutlined
                    className="review-form-history-icon"
                    onClick={() => setOpen(true)}
                  />
                )}
              </span>
            </div>
            <div className="review-form-title-actions">
              {referralFormData?.id && (
                <Button
                  type="primary"
                  onClick={() =>
                    printFormSummary("review", referralFormData?.id)
                  }
                >
                  Print Summary
                </Button>
              )}
              {!editable && isActive && !["receptionist"]?.includes(role) && (
                <Button
                  onClick={() => handleNewForm()}
                  type="primary"
                  ghost
                >
                  New Form
                </Button>
              )}
            </div>
          </Typography.Title>
          {open && (
            <ReviewModal
              open={open}
              setOpen={setOpen}
              reviewList={reviewList}
              onShowClick={(record) => handleModalAction(record, false)}
              onEditClick={(record) => handleModalAction(record, true)}
              form={form}
              isActive={isActive}
            />
          )}
          <FormContentLoader
            loading={!isContentReady}
            tip="Loading review form..."
          >
          {canEditForm ? (
            <Form
              form={form}
              ref={formRef}
              layout="vertical"
              onFinish={onFinish}
              className="forms-ip-shell"
              initialValues={{
                ...referralFormData?.data,
                clinical_progress: {
                  ...referralFormData?.data?.clinical_progress,
                  date: referralFormData?.data?.clinical_progress?.date
                    ? dayjs(referralFormData?.data?.clinical_progress?.date) // Keep as dayjs object
                    : null,
                },
              }}
            >
              <Collapse
                expandIconPosition="right"
                className="custom-collapse"
                defaultActiveKey={[
                  "1",
                  "2",
                  "3",
                  "4",
                  "5",
                  "6",
                  "7",
                  "8",
                  "9",
                  "10",
                  "11",
                ]}
              >
                <Collapse.Panel
                  header="Patient Profile"
                  style={commonStyle}
                  key="1"
                >
                  <PatientProfile data={patientData?.admission} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Consultants"
                  style={commonStyle}
                  key="2"
                >
                  <ConsultantForm form={form} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Impression/ Diagnosis"
                  style={commonStyle}
                  key="3"
                >
                  <ClinicalImpression
                    form={form}
                    details={details?.data?.diagnosis}
                  />
                </Collapse.Panel>
                <Collapse.Panel
                  header="History of Present Illness"
                  style={commonStyle}
                  key="4"
                >
                  <HistoryIllness
                    form={form}
                    patientData={patientData?.admission}
                    data={details?.data?.present_illness}
                  />
                </Collapse.Panel>
                {isPhysio && (
                  <Collapse.Panel
                    header="Scales"
                    style={commonStyle}
                    key="5"
                  >
                    <ReviewFormScales
                      admissionId={patientData?.admission?.id}
                    />
                  </Collapse.Panel>
                )}
                {/* <Collapse.Panel
              header="Functional Status at the time of Admission"
              style={commonStyle}
              key="5"
            >
              <FunctionalStatus form={form} details={details} />
            </Collapse.Panel> */}
                <Collapse.Panel
                  header="Clinical Progress"
                  style={commonStyle}
                  key="6"
                >
                  <ReviewClinicalProgress
                    form={form}
                    setPressureSorePoints={setPressureSorePoints}
                    pressureSorePoints={pressureSorePoints}
                    hidePressureSore={isPhysio}
                  />
                </Collapse.Panel>
                {!isPhysio && (
                  <>
                    <Collapse.Panel
                      header="Medication"
                      style={commonStyle}
                      key="7"
                    >
                      <Medication form={form} details={details} />
                    </Collapse.Panel>
                    <Collapse.Panel
                      header="Nebulization"
                      style={commonStyle}
                      key="8"
                    >
                      <Nebulization form={form} details={details} />
                    </Collapse.Panel>
                  </>
                )}
                <Collapse.Panel
                  header={functionalStatusHeader}
                  style={commonStyle}
                  key="9"
                >
                  <DischargeFunctionalStatus form={form} />
                </Collapse.Panel>
                {!isPhysio && (
                  <Collapse.Panel header="Vitals" style={commonStyle} key="10">
                    <Vitals form={form} details={details} />
                  </Collapse.Panel>
                )}
                <Collapse.Panel
                  header="Comments"
                  style={{ ...commonStyle, marginBottom: "40px" }}
                  key="11"
                >
                  <Comments form={form} />
                </Collapse.Panel>
              </Collapse>
              <div
                className="ip-fixed-form-footer"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,

                  width: "100%",
                  background: "#fff",
                  paddingTop: "10px",
                  paddingRight: "16px",
                  boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
                  textAlign: "right",
                  zIndex: 1000, // Ensure it stays above other elements
                }}
              >
                {isActive && !["receptionist"]?.includes(role) && (
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className={btnStyles.lgBtn}
                    >
                      {referralFormData?.id ? "Update" : "Submit"}
                    </Button>
                  </Form.Item>
                )}
              </div>
            </Form>
          ) : !isActive && !referralFormData?.id ? (
            <NoDataPage
              title={`${formTitle} Not Available`}
              subtitle={`${formTitle} is not available for discharged patients`}
              image={assessmentIcon}
            />
          ) : isReceptionist && !referralFormData?.id ? (
            <NoDataPage
              title={`${formTitle} Not Available`}
              subtitle={`${formTitle} has not been submitted yet`}
              image={assessmentIcon}
            />
          ) : displayForm ? (
            <ScaledHtmlPreview
              html={displayForm}
              designWidth={900}
              className="ip-form-html-preview"
            />
          ) : referralFormData?.id ? (
            <FormContentLoader loading tip="Loading review summary..." />
          ) : null}
          </FormContentLoader>
        </div>
      )}
    </>
  );
};

export default ReviewForms;
